import { useMemo } from 'react';
import { NewQuote } from 'new/new-quote';
import { useSnackbar } from 'notistack';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import { ROUTES } from 'routing/routes';
import { ProvideNewQuoteState } from 'shared/contexts';
import { useBrandRedirect } from 'shared/hooks';
import { useAuthState } from 'shared/stores';
import { Brands, ProductLookup, Quote } from 'shared/types';
import { LookupRecord } from '@price-for-profit/micro-services';

interface IQuoteToCopyState {
  state:
    | {
        quoteToCopy: Quote;
        productsToCopy: LookupRecord<ProductLookup>[];
      }
    | undefined;
}

export function NewQuoteResolver() {
  const { brand } = useParams<{ brand: Brands }>();
  const { state } = useLocation<IQuoteToCopyState>();
  const authState = useAuthState();
  const user = authState.user.get();

  const quoteToCopy = useMemo(() => state?.state?.quoteToCopy, [state]);
  const productsToCopy = useMemo(() => state?.state?.productsToCopy, [state]);
  const isQuoteToCopy = useMemo(() => !!quoteToCopy, [quoteToCopy]);

  const [redirect] = useBrandRedirect(user?.insightClaims || '', sites => !sites.includes(brand));
  const { enqueueSnackbar } = useSnackbar();

  if (!user) {
    return null;
  }
  if (redirect) {
    enqueueSnackbar(`You are not allowed to create the quotes for this brand: ${brand}`, { variant: 'error' });
    return <Redirect to={ROUTES.brands} />;
  }

  return (
    <ProvideNewQuoteState>
      <NewQuote
        brand={brand}
        user={user}
        isQuoteToCopy={isQuoteToCopy}
        quoteToCopy={quoteToCopy}
        productsToCopy={productsToCopy}
      />
    </ProvideNewQuoteState>
  );
}
